import { PortalPartnerTypeEnum } from '@escapenavigator/types/dist/partners-portal/enum/portal-partner-type.enum';
import { PortalPartnerStatsRO } from '@escapenavigator/types/dist/partners-portal/portal-stats.ro';
import { BarChart } from '@mantine/charts';
import {
    Alert,
    Card,
    Center,
    Grid,
    Group,
    Loader,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
} from '@mantine/core';
import {
    IconCoins,
    IconDeviceGamepad2,
    IconInfoCircle,
    IconPercentage,
    IconQrcode,
} from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';

import { api } from '../api';
import { PageHeader } from '../components/page-header';
import { t } from '../dictionary';
import { useAuth } from '../providers/auth-context';
import { formatMoneyMap } from '../utils/money';

const StatCard = ({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
}) => (
    <Card padding='lg'>
        <Group wrap='nowrap'>
            <ThemeIcon size={42} radius='md' variant='light' color='indigo'>
                {icon}
            </ThemeIcon>
            <div style={{ minWidth: 0 }}>
                <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                    {label}
                </Text>
                <Text fw={700} size='xl' truncate>
                    {value}
                </Text>
            </div>
        </Group>
    </Card>
);

export const DashboardPage = () => {
    const { partner } = useAuth();
    const [stats, setStats] = useState<PortalPartnerStatsRO | null>(null);

    useEffect(() => {
        api.stats()
            .then(({ data }) => setStats(data))
            .catch(() => setStats(null));
    }, []);

    const isAuthor = partner.type === PortalPartnerTypeEnum.AUTHOR;

    if (!stats) {
        return (
            <Center h={300}>
                <Loader />
            </Center>
        );
    }

    const chartData = stats.monthly.map((m) => ({
        month: new Date(m.month).toLocaleString('en', { month: 'short' }),
        Games: m.games,
    }));

    const hasSales = stats.totalGames > 0;

    return (
        <>
            <PageHeader
                title={t.dashboard.welcome(partner.name)}
                subtitle={isAuthor ? t.dashboard.authorSubtitle : t.dashboard.venueSubtitle}
            />

            <Stack gap='lg'>
                {partner.commissionPercent === 0 && (
                    <Alert
                        variant='light'
                        color='indigo'
                        icon={<IconInfoCircle />}
                        title={t.dashboard.commissionPendingTitle}
                    >
                        {t.dashboard.commissionPendingText}
                    </Alert>
                )}

                <SimpleGrid cols={{ base: 1, xs: 2, md: isAuthor ? 3 : 4 }}>
                    <StatCard
                        icon={<IconPercentage size={22} />}
                        label={t.dashboard.commission}
                        value={`${stats.commissionPercent}%`}
                    />
                    <StatCard
                        icon={<IconDeviceGamepad2 size={22} />}
                        label={t.dashboard.gamesPlayed}
                        value={String(stats.totalGames)}
                    />
                    {!isAuthor && (
                        <StatCard
                            icon={<IconQrcode size={22} />}
                            label={t.dashboard.qrScans}
                            value={String(stats.qrScans ?? 0)}
                        />
                    )}
                    <StatCard
                        icon={<IconCoins size={22} />}
                        label={t.dashboard.totalEarned}
                        value={formatMoneyMap(stats.totalCommission)}
                    />
                </SimpleGrid>

                <Grid>
                    <Grid.Col span={12}>
                        <Card padding='lg'>
                            <Text fw={650} mb='md'>
                                {t.dashboard.monthlyChart}
                            </Text>
                            {hasSales ? (
                                <BarChart
                                    h={260}
                                    data={chartData}
                                    dataKey='month'
                                    series={[{ name: 'Games', color: 'indigo.6' }]}
                                    withLegend={false}
                                    gridAxis='y'
                                    barProps={{ radius: 6 }}
                                />
                            ) : (
                                <Center h={200}>
                                    <Stack align='center' gap={4}>
                                        <Text fw={600}>{t.dashboard.noSalesYet}</Text>
                                        <Text size='sm' c='dimmed' ta='center' maw={420}>
                                            {isAuthor
                                                ? t.dashboard.noSalesAuthorText
                                                : t.dashboard.noSalesVenueText}
                                        </Text>
                                    </Stack>
                                </Center>
                            )}
                        </Card>
                    </Grid.Col>
                </Grid>
            </Stack>
        </>
    );
};
